import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { formStyles } from './shared-styles.js';
import { AuthService, authService as defaultAuthService } from '../services/auth-service.js';
import { isApiError } from '../services/api-client.js';
import type { LoginResult, UserInfo } from '../models/auth.js';

/**
 * Email + password sign-in form with two-factor support. On a successful sign-in it dispatches a
 * bubbling, composed `gc-auth-success` CustomEvent whose detail is the signed-in {@link UserInfo}.
 *
 * If the account has 2FA enabled, the password step resolves with `requiresTwoFactor: true`; the
 * form then shows a second step to enter an authenticator code (with a fallback to a recovery
 * code) before completing sign-in. Errors are rendered inline. The {@link authService} can be
 * overridden (e.g. in tests) via the `.authService` property.
 */
@customElement('gc-login-form')
export class GcLoginForm extends LitElement {
  static override styles = formStyles;

  /** Injectable for testing; defaults to the shared AuthService. */
  authService: AuthService = defaultAuthService;

  @property({ type: String })
  email = '';

  @state()
  private loading = false;

  @state()
  private error = '';

  /** True once the password step reports the account has two-factor authentication enabled. */
  @state()
  private requiresTwoFactor = false;

  /** Toggles the second step between the authenticator-code and recovery-code forms. */
  @state()
  private useRecoveryCode = false;

  /** Set when sign-in was refused because the email isn't confirmed; reveals a resend option. */
  @state()
  private emailNotConfirmed = false;

  /** Set once a confirmation email has been re-sent from this form. */
  @state()
  private confirmationResent = false;

  /** In Development, a click-to-confirm link returned by resend (null in production). */
  @state()
  private devConfirmUrl: string | null = null;

  override render() {
    if (this.requiresTwoFactor) {
      return this.useRecoveryCode ? this.renderRecoveryStep() : this.renderTwoFactorStep();
    }
    return this.renderPasswordStep();
  }

  private renderPasswordStep(): TemplateResult {
    return html`
      <form @submit=${this.onPasswordSubmit}>
        <label>
          Email
          <input
            type="email"
            name="email"
            autocomplete="username"
            required
            .value=${this.email}
            @input=${(e: Event) => (this.email = (e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Password
          <input type="password" name="password" autocomplete="current-password" required />
        </label>
        <div class="error" role="alert">${this.error}</div>
        ${this.emailNotConfirmed
          ? html`<div class="resend">
              ${this.confirmationResent
                ? html`<span class="success" role="status">Confirmation email sent. Check your inbox.</span>
                    ${this.devConfirmUrl
                      ? html`<div>Development environment: <a href=${this.devConfirmUrl}>click here to confirm</a>.</div>`
                      : nothing}`
                : html`<button type="button" class="link" @click=${this.onResendConfirmation}>
                    Resend confirmation email
                  </button>`}
            </div>`
          : nothing}
        <button type="submit" ?disabled=${this.loading}>
          ${this.loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    `;
  }

  private renderTwoFactorStep(): TemplateResult {
    return html`
      <form @submit=${this.onTwoFactorSubmit}>
        <p>Enter the code from your authenticator app.</p>
        <label>
          Authenticator code
          <input
            type="text"
            name="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            required
          />
        </label>
        <label class="checkbox">
          <input type="checkbox" name="rememberMachine" />
          Remember this device
        </label>
        <div class="error" role="alert">${this.error}</div>
        <button type="submit" ?disabled=${this.loading}>
          ${this.loading ? 'Verifying…' : 'Verify'}
        </button>
        <button type="button" class="link" @click=${this.showRecoveryCode}>
          Use a recovery code instead
        </button>
      </form>
    `;
  }

  private renderRecoveryStep(): TemplateResult {
    return html`
      <form @submit=${this.onRecoverySubmit}>
        <p>Enter one of your saved recovery codes.</p>
        <label>
          Recovery code
          <input type="text" name="recoveryCode" autocomplete="one-time-code" required />
        </label>
        <div class="error" role="alert">${this.error}</div>
        <button type="submit" ?disabled=${this.loading}>
          ${this.loading ? 'Verifying…' : 'Verify'}
        </button>
        <button type="button" class="link" @click=${this.showAuthenticatorCode}>
          Use your authenticator app instead
        </button>
      </form>
    `;
  }

  private async onPasswordSubmit(event: Event) {
    event.preventDefault();
    if (this.loading) {
      return;
    }
    const data = new FormData(event.target as HTMLFormElement);
    const email = String(data.get('email') ?? '');
    const password = String(data.get('password') ?? '');

    await this.attempt(
      () => this.authService.login(email, password),
      'Sign in failed. Please try again.',
    );
  }

  private async onTwoFactorSubmit(event: Event) {
    event.preventDefault();
    if (this.loading) {
      return;
    }
    const data = new FormData(event.target as HTMLFormElement);
    const code = String(data.get('code') ?? '');
    const rememberMachine = data.get('rememberMachine') !== null;

    await this.attempt(
      () => this.authService.loginWith2fa(code, true, rememberMachine),
      'Verification failed. Please try again.',
    );
  }

  private async onRecoverySubmit(event: Event) {
    event.preventDefault();
    if (this.loading) {
      return;
    }
    const data = new FormData(event.target as HTMLFormElement);
    const recoveryCode = String(data.get('recoveryCode') ?? '');

    await this.attempt(
      () => this.authService.loginWithRecoveryCode(recoveryCode),
      'Verification failed. Please try again.',
    );
  }

  /** Runs an auth call, applying the result/error to the shared loading + error + step state. */
  private async attempt(call: () => Promise<LoginResult>, fallbackError: string) {
    this.error = '';
    this.emailNotConfirmed = false;
    this.confirmationResent = false;
    this.loading = true;
    try {
      this.handleLoginResult(await call());
    } catch (err) {
      this.error = isApiError(err) ? err.message : fallbackError;
      // 403 from the password step means the email isn't confirmed; offer to resend the link.
      if (isApiError(err) && err.status === 403) {
        this.emailNotConfirmed = true;
      }
    } finally {
      this.loading = false;
    }
  }

  private onResendConfirmation = async (): Promise<void> => {
    try {
      const result = await this.authService.resendConfirmation(this.email);
      this.devConfirmUrl = result?.devConfirmUrl ?? null;
    } catch {
      // The endpoint always succeeds from the user's perspective; ignore failures.
    } finally {
      this.confirmationResent = true;
    }
  };

  private handleLoginResult(result: LoginResult) {
    if (result.requiresTwoFactor) {
      // Move to (or stay on) the 2FA step and clear any prior error.
      this.requiresTwoFactor = true;
      this.error = '';
      return;
    }
    if (result.user) {
      this.dispatchEvent(
        new CustomEvent<UserInfo>('gc-auth-success', {
          detail: result.user,
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private showRecoveryCode() {
    this.useRecoveryCode = true;
    this.error = '';
  }

  private showAuthenticatorCode() {
    this.useRecoveryCode = false;
    this.error = '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-login-form': GcLoginForm;
  }
}
